import { motion } from "framer-motion";
import { CheckCircle, ArrowRight } from "lucide-react";
import { Button } from "../../components/ui/Button";
import { openWhatsApp } from "../../utils/whatsapp";
import { SEO } from "../../components/SEO";
import { useEffect } from "react";

export default function FlightBookingPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const benefits = [
    "Domestic and international flight bookings",
    "Competitive pricing and best deals",
    "Group travel arrangements and discounts",
    "Special student travel rates",
    "Flexible booking and modification options",
    "24/7 customer support and assistance",
  ];

  const process = [
    {
      step: "01",
      title: "Share Your Details",
      description:
        "Tell us your travel dates, destinations, preferences, and any special requirements you may have.",
    },
    {
      step: "02",
      title: "Get Best Options",
      description:
        "We search across multiple airlines to find you the best routes and prices that match your needs.",
    },
    {
      step: "03",
      title: "Review & Confirm",
      description:
        "Review your flight options, compare prices, and select the one that works best for you.",
    },
    {
      step: "04",
      title: "Secure Booking",
      description:
        "We handle the booking process securely and send you all necessary travel documents and confirmations.",
    },
    {
      step: "05",
      title: "Travel Support",
      description:
        "Get ongoing support for changes, updates, or any assistance you need before and during your journey.",
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Flight Booking"
        description="Book domestic and international flights with BeginPack. Best prices on airline tickets, group travel discounts, student rates, and 24/7 travel support."
        ogTitle="Flight Ticket Booking - Domestic & International Travel"
        ogDescription="Book your flights with ease. We offer competitive pricing, student discounts, group travel arrangements, and 24/7 support for all your travel needs."
      />
      {/* Hero Section */}
      <div className="relative bg-cover bg-center bg-no-repeat py-20 bg-gradient-to-br from-[#004E89] to-[#800020] md:py-28 lg:py-32">
        <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-poppins text-4xl md:text-6xl font-bold tracking-tight leading-tight antialiased mb-4 text-white">
            Flight Ticket Booking
          </h1>
          <p className="font-jakarta text-lg md:text-xl tracking-tight leading-relaxed antialiased max-w-2xl mx-auto text-white">
            Whether you're flying for studies, work, vacation, or visiting
            family, we make booking your flights simple and stress-free. Our
            travel experts find you the best deals, handle all the details, and
            provide support throughout your journey so you can focus on what
            matters most.
          </p>
        </div>
      </div>

      {/* Overview Section */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="font-poppins text-3xl md:text-5xl font-bold text-gray-900 mb-6 tracking-tight antialiased">
              Your Travel Made Simple
            </h2>
            <p className="font-jakarta text-lg text-gray-600 tracking-tight leading-relaxed antialiased">
              We offer fast, reliable, and affordable flight ticket booking
              services for students, travellers, and families. Whether you are
              flying for education, work, or leisure, we help you find the best
              fares, routes, and airlines to suit your schedule and budget. From
              ticket reservations and itinerary planning to fare comparisons and
              airline guidance, we handle the process efficiently, ensuring a
              smooth and hassle-free travel experience from start to finish.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-8 md:py-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-poppins text-3xl md:text-4xl font-bold text-gray-900 mb-4 tracking-tight antialiased">
              What We Offer
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex items-start gap-4 bg-gray-50 p-6 rounded-xl"
              >
                <CheckCircle className="w-6 h-6 text-[#800020] flex-shrink-0 mt-1" />
                <span className="font-jakarta text-gray-700 text-lg tracking-tight antialiased">
                  {benefit}
                </span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-poppins text-3xl md:text-4xl font-bold text-gray-900 mb-4 tracking-tight antialiased">
              Our Process
            </h2>
            <p className="font-jakarta text-lg text-gray-600 max-w-2xl mx-auto tracking-tight leading-relaxed antialiased">
              Book your flight in 5 easy steps
            </p>
          </motion.div>

          <div className="space-y-6">
            {process.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl p-6 md:p-8 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex flex-col md:flex-row md:items-center gap-6">
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#800020] to-[#600018] flex items-center justify-center">
                      <span className="font-poppins text-white text-xl font-bold">
                        {item.step}
                      </span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-poppins text-2xl font-bold text-gray-900 mb-2 tracking-tight antialiased">
                      {item.title}
                    </h3>
                    <p className="font-jakarta text-gray-600 tracking-tight leading-relaxed antialiased">
                      {item.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20 bg-gradient-to-r from-[#004E89] to-[#800020]">
        <div className="max-w-4xl mx-auto text-center px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-poppins text-3xl md:text-5xl font-bold text-white mb-6 tracking-tight antialiased">
              Ready to Book Your Flight?
            </h2>
            <p className="font-jakarta text-lg md:text-xl text-white/90 mb-8 tracking-tight antialiased">
              Get in touch with our travel experts and let us find you the best
              deals for your journey.
            </p>
            <Button
              variant="primary"
              size="lg"
              rightIcon={<ArrowRight className="w-5 h-5" />}
              onClick={() =>
                openWhatsApp(
                  "Hello! I would like to book a flight with BeginPack Ltd.\n\nName:\nEmail:\nPhone:\nTravel details:",
                )
              }
            >
              Contact Us
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
